import { CookieService } from './cookies';

export type CookieConsentStatus = 'accepted' | 'refused' | 'configured';

export interface CookieConsentPreferences {
  audienceMeasurement: boolean;
  communicationTracking: boolean;
}

const COOKIE_CONSENT_COOKIE = 'palenCookieConsent';
const COOKIE_CONSENT_EVENT = 'palen-cookie-consent-updated';
const COOKIE_PREFERENCES_REQUEST_EVENT = 'palen-cookie-preferences-requested';
const COOKIE_CONSENT_DURATION_DAYS = 180;
const REFUSED_PREFERENCES: CookieConsentPreferences = {
  audienceMeasurement: false,
  communicationTracking: false,
};
const ACCEPTED_PREFERENCES: CookieConsentPreferences = {
  audienceMeasurement: true,
  communicationTracking: true,
};
const DEFAULT_PREFERENCES: CookieConsentPreferences = {
  audienceMeasurement: true,
  communicationTracking: false,
};

class CookieConsentService {
  readStatus(): CookieConsentStatus | null {
    return this.readConsent()?.status ?? null;
  }

  readPreferences(): CookieConsentPreferences {
    return this.readConsent()?.preferences ?? DEFAULT_PREFERENCES;
  }

  accept(): void {
    this.saveConsent('accepted', ACCEPTED_PREFERENCES);
  }

  refuse(): void {
    this.saveConsent('refused', REFUSED_PREFERENCES);
  }

  configure(preferences: CookieConsentPreferences): void {
    this.saveConsent('configured', preferences);
  }

  canUseAnalytics(): boolean {
    return this.canUseAudienceMeasurement();
  }

  canUseOptionalCookies(): boolean {
    const preferences = this.readPreferences();
    return preferences.audienceMeasurement || preferences.communicationTracking;
  }

  canUseAudienceMeasurement(): boolean {
    return this.readPreferences().audienceMeasurement;
  }

  canUseCommunicationTracking(): boolean {
    return this.readPreferences().communicationTracking;
  }

  onChange(listener: () => void): () => void {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    window.addEventListener(COOKIE_CONSENT_EVENT, listener);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, listener);
  }

  requestPreferences(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(new Event(COOKIE_PREFERENCES_REQUEST_EVENT));
  }

  onPreferencesRequested(listener: () => void): () => void {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    window.addEventListener(COOKIE_PREFERENCES_REQUEST_EVENT, listener);
    return () => window.removeEventListener(COOKIE_PREFERENCES_REQUEST_EVENT, listener);
  }

  private readConsent(): { status: CookieConsentStatus; preferences: CookieConsentPreferences } | null {
    const value = CookieService.get(COOKIE_CONSENT_COOKIE);
    if (value === 'accepted') {
      return { status: 'accepted', preferences: ACCEPTED_PREFERENCES };
    }

    if (value === 'refused') {
      return { status: 'refused', preferences: REFUSED_PREFERENCES };
    }

    try {
      const parsed = JSON.parse(decodeURIComponent(value || ''));
      if (parsed?.status === 'configured' && this.isPreferences(parsed.preferences)) {
        return { status: 'configured', preferences: parsed.preferences };
      }
    } catch {
      return null;
    }

    return null;
  }

  private isPreferences(value: unknown): value is CookieConsentPreferences {
    const preferences = value as CookieConsentPreferences;
    return typeof preferences?.audienceMeasurement === 'boolean'
      && typeof preferences?.communicationTracking === 'boolean';
  }

  private saveConsent(status: CookieConsentStatus, preferences: CookieConsentPreferences): void {
    const value = status === 'configured'
      ? encodeURIComponent(JSON.stringify({ status, preferences }))
      : status;
    CookieService.set(COOKIE_CONSENT_COOKIE, value, COOKIE_CONSENT_DURATION_DAYS);
    this.dispatchChange();
  }

  private dispatchChange(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
  }
}

export const cookieConsentService = new CookieConsentService();
